import { useState } from 'react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { PatientSelector } from '../download/PatientSelector';
import { RTPFilterForm } from './RTPFilterForm';
import { RTPDownloadActions } from './RTPDownloadActions';
import { RTPPreviewTable } from './RTPPreviewTable';
import { usePatients } from '@/hooks/usePatients';
import { useRTPDownloadConfig } from '@/hooks/useRTPDownloadConfig';
import { useRTPPreviewDownload } from '@/hooks/useRTPPreviewDownload';
import { downloadDoctorRTP } from '@/api/rtpDownload';
import { downloadZip, generateDownloadFilename, extractTokenFromUrl } from '@/utils/download';
import { createRtpCsvZip } from '@/utils/zipDownload';
import { useToast } from '@/contexts/toastContextBase';
import { getPatientFullName } from '@/types/patient';
import type {
  DownloadProgress,
  RTPDownloadResult,
} from '@/types/rtpDownload';

export function RTPDownloadBuilder() {
  const { patients, isLoading: isLoadingPatients } = usePatients();
  const { showToast } = useToast();

  const [selectedPatientIds, setSelectedPatientIds] = useState<string[]>([]);
  const {
    timeRange,
    grouping,
    onTimeRangeChange,
    onGroupingChange,
    hasTimeRange,
  } = useRTPDownloadConfig();
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    status: 'idle',
  });

  const currentPatientIds =
    selectedPatientIds.length > 0 ? selectedPatientIds : patients.map((p) => p.id);

  const {
    cachedResults,
    previewData,
    previewState,
    selectedPreviewPatientIds,
    setSelectedPreviewPatientIds,
    loadPreview,
    isPreviewStale,
  } = useRTPPreviewDownload({
    timeRange,
    grouping,
    patientIds: currentPatientIds,
    fetchResults: async () => {
      const patientsToPreview =
        selectedPatientIds.length > 0
          ? patients.filter((p) => selectedPatientIds.includes(p.id))
          : patients;

      if (patientsToPreview.length === 0) {
        return new Map();
      }

      const patientList = patientsToPreview.map((p) => ({
        id: p.id,
        name: getPatientFullName(p),
        token: extractTokenFromUrl(p.apiToken),
      }));

      const { successful } = await downloadDoctorRTP(
        {
          patientIds: patientList.map((p) => p.id),
          timeRange: {
            from: timeRange.from!,
            to: timeRange.to!,
          },
          grouping,
        },
        patientList
      );

      const resultsMap = new Map<string, RTPDownloadResult>();
      successful.forEach((result) => {
        resultsMap.set(result.patientId, result);
      });

      return resultsMap;
    },
  });

  const handlePreview = async () => {
    if (!timeRange.from || !timeRange.to) {
      showToast('error', 'Please select a time range');
      return;
    }

    if (currentPatientIds.length === 0) {
      showToast('error', 'No patients available');
      return;
    }

    try {
      const { count } = await loadPreview();
      if (count === 0) {
        showToast('warning', 'No RTP data found for selected patients in this time range');
      } else {
        showToast('success', `Preview loaded: ${count} patients with RTP data`);
      }
    } catch {
      showToast('error', 'Failed to load preview');
    }
  };

  const handleDownload = async () => {
    if (!cachedResults || selectedPreviewPatientIds.size === 0) {
      showToast('error', 'Please preview and select patients first');
      return;
    }

    if (isPreviewStale()) {
      showToast('error', 'Preview data is outdated. Please refresh preview.');
      return;
    }

    try {
      setDownloadProgress({ status: 'processing', message: 'Preparing download...' });

      // Get selected cached results
      const selectedResults: RTPDownloadResult[] = [];
      selectedPreviewPatientIds.forEach((patientId) => {
        const result = cachedResults.get(patientId);
        if (result) {
          selectedResults.push(result);
        }
      });

      const zipBlob = await createRtpCsvZip(selectedResults);
      const filename = generateDownloadFilename('rtp-download', 'zip');
      downloadZip(zipBlob, filename);

      setDownloadProgress({ status: 'complete' });
      showToast(
        'success',
        `Downloaded RTP data for ${selectedResults.length} patient${selectedResults.length === 1 ? '' : 's'}`
      );

      // Reset progress after a delay
      setTimeout(() => {
        setDownloadProgress({ status: 'idle' });
      }, 3000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to download data';
      setDownloadProgress({ status: 'error', message });
      showToast('error', message);

      // Reset progress after a delay
      setTimeout(() => {
        setDownloadProgress({ status: 'idle' });
      }, 5000);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Build Your Selection"
          description="Select patients, time range, and grouping for RTP data"
        />
        <CardBody className="space-y-6">
          <PatientSelector
            patients={patients}
            selectedIds={selectedPatientIds}
            onChange={setSelectedPatientIds}
            isLoading={isLoadingPatients}
          />

          <RTPFilterForm
            timeRange={timeRange}
            grouping={grouping}
            onTimeRangeChange={onTimeRangeChange}
            onGroupingChange={onGroupingChange}
          />
        </CardBody>
      </Card>

      {previewData && previewData.size > 0 && (
        <Card>
          <CardHeader
            title="Preview Results"
            description={`${previewData.size} patients with RTP data found`}
          />
          <CardBody>
            <RTPPreviewTable
              previewData={previewData}
              patients={patients}
              selectedPatientIds={selectedPreviewPatientIds}
              onTogglePatient={(id) => {
                const newSet = new Set(selectedPreviewPatientIds);
                if (newSet.has(id)) {
                  newSet.delete(id);
                } else {
                  newSet.add(id);
                }
                setSelectedPreviewPatientIds(newSet);
              }}
              onSelectAll={() => {
                setSelectedPreviewPatientIds(new Set(previewData.keys()));
              }}
              onDeselectAll={() => {
                setSelectedPreviewPatientIds(new Set());
              }}
            />
          </CardBody>
        </Card>
      )}

      <RTPDownloadActions
        hasPreviewData={cachedResults !== null}
        isLoadingPreview={previewState.isLoading}
        previewError={previewState.error}
        onPreview={handlePreview}
        canPreview={hasTimeRange}
        selectedPreviewPatientIds={selectedPreviewPatientIds}
        onDownload={handleDownload}
        isDownloading={downloadProgress.status === 'processing'}
        isPreviewStale={isPreviewStale()}
        timeRange={timeRange}
        grouping={grouping}
        patientCount={selectedPatientIds.length || patients.length}
      />
    </div>
  );
}
